/**
 * Team Members Tab Component
 *
 * Displays account users — read-only view for Super Admin.
 */
import { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Badge } from '@respectly/ui';
import { Mail, Shield, Calendar, Users } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useAccountsStore } from '../../../stores/useAccountsStore';

interface UsersTabProps {
  accountId: string;
}

// ── Avatar color palette ─────────────────────────────────────────
const AVATAR_GRADIENTS = [
  { from: 'from-violet-500', to: 'to-violet-700', text: 'text-white' },
  { from: 'from-sky-500',    to: 'to-sky-700',    text: 'text-white' },
  { from: 'from-emerald-500',to: 'to-emerald-700', text: 'text-white' },
  { from: 'from-amber-400',  to: 'to-amber-600',  text: 'text-white' },
  { from: 'from-rose-500',   to: 'to-rose-700',   text: 'text-white' },
  { from: 'from-indigo-500', to: 'to-indigo-700', text: 'text-white' },
  { from: 'from-teal-500',   to: 'to-teal-700',   text: 'text-white' },
];

function getAvatarGradient(name: string) {
  const idx = (name.charCodeAt(0) + (name.charCodeAt(1) || 0)) % AVATAR_GRADIENTS.length;
  return AVATAR_GRADIENTS[idx] ?? AVATAR_GRADIENTS[0]!;
}

// ── Role badge styling ───────────────────────────────────────────
function getRoleStyle(role: string): { label: string; className: string } {
  const r = role.toLowerCase();
  if (r.includes('admin')) {
    return {
      label: role,
      className:
        'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 border border-violet-200/60 dark:border-violet-700/40',
    };
  }
  return {
    label: role,
    className:
      'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-600',
  };
}

// ── Loading skeleton ─────────────────────────────────────────────
function LoadingSkeleton() {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="h-5 w-5 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
          <div className="h-5 w-36 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
        </div>
        <div className="mt-1 h-3 w-28 rounded bg-slate-100 dark:bg-slate-800 animate-pulse" />
      </CardHeader>
      <CardContent className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="flex items-center gap-4 rounded-xl border border-slate-100 dark:border-slate-800 p-4 animate-pulse"
          >
            <div className="h-11 w-11 rounded-full bg-slate-200 dark:bg-slate-700 shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <div className="h-4 w-32 rounded bg-slate-200 dark:bg-slate-700" />
                <div className="h-4 w-20 rounded-md bg-slate-100 dark:bg-slate-800" />
              </div>
              <div className="h-3 w-48 rounded bg-slate-100 dark:bg-slate-800" />
            </div>
            <div className="hidden sm:flex flex-col items-end gap-1.5">
              <div className="h-4 w-14 rounded-full bg-slate-100 dark:bg-slate-800" />
              <div className="h-3 w-24 rounded bg-slate-100 dark:bg-slate-800" />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ── Empty state ──────────────────────────────────────────────────
function EmptyState() {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-16">
        <div className="rounded-2xl bg-slate-100 dark:bg-slate-800 p-4">
          <Users className="h-10 w-10 text-slate-400" />
        </div>
        <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
          No Team Members
        </h3>
        <p className="mt-1 text-center text-sm text-slate-500 max-w-sm">
          This account has no users assigned yet.
        </p>
      </CardContent>
    </Card>
  );
}

// ── Main component ───────────────────────────────────────────────
export function UsersTab({ accountId }: UsersTabProps) {
  const { accountUsers, isLoadingUsers, loadAccountUsers } = useAccountsStore();

  useEffect(() => {
    loadAccountUsers(accountId);
  }, [accountId, loadAccountUsers]);

  if (isLoadingUsers) return <LoadingSkeleton />;
  if (accountUsers.length === 0) return <EmptyState />;

  const activeCount   = accountUsers.filter((u) => u.isActive).length;
  const inactiveCount = accountUsers.length - activeCount;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-slate-500" />
          <CardTitle className="text-base">
            Team Members
            <span className="ml-2 text-sm font-normal text-slate-400">
              ({accountUsers.length})
            </span>
          </CardTitle>
        </div>

        {/* Stats summary */}
        <p className="text-xs text-slate-500 dark:text-slate-400">
          <span className="text-emerald-600 dark:text-emerald-400 font-medium">
            {activeCount} active
          </span>
          {inactiveCount > 0 && (
            <>
              <span className="mx-1.5 text-slate-300 dark:text-slate-600">·</span>
              <span>{inactiveCount} inactive</span>
            </>
          )}
        </p>
      </CardHeader>

      <CardContent className="space-y-2.5">
        {accountUsers.map((user) => {
          const fullName = `${user.firstName} ${user.lastName}`;
          const gradient = getAvatarGradient(fullName);
          const role = getRoleStyle(user.role);
          const lastLogin = user.lastLoginAt
            ? formatDistanceToNow(new Date(user.lastLoginAt), { addSuffix: true })
            : null;
          const lastLoginFull = user.lastLoginAt
            ? new Date(user.lastLoginAt).toLocaleDateString(undefined, {
                year: 'numeric', month: 'long', day: 'numeric',
              })
            : null;

          return (
            <div
              key={user.id}
              className={[
                'group flex items-center gap-4 rounded-xl border p-4 transition-colors',
                user.isActive
                  ? 'border-slate-100 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/40 hover:border-slate-200 dark:hover:border-slate-700'
                  : 'border-slate-100 dark:border-slate-800/60 bg-slate-50/40 dark:bg-slate-900/20 opacity-75',
              ].join(' ')}
            >
              {/* Avatar */}
              <div
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${gradient.from} ${gradient.to} ${gradient.text} text-sm font-bold shadow-sm`}
              >
                {user.firstName.charAt(0).toUpperCase()}
                {user.lastName.charAt(0).toUpperCase()}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-900 dark:text-white leading-tight">
                    {fullName}
                  </span>
                  <span className={role.className}>
                    <Shield className="h-3 w-3" />
                    {role.label}
                  </span>
                </div>
                <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 truncate">
                  <Mail className="h-3 w-3 shrink-0" />
                  <span className="truncate">{user.email}</span>
                </div>
              </div>

              {/* Right: status + last login */}
              <div className="hidden sm:flex flex-col items-end gap-1.5 shrink-0">
                <Badge
                  variant={user.isActive ? 'default' : 'secondary'}
                  className={[
                    'text-xs font-medium',
                    user.isActive
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border-0 hover:bg-emerald-100'
                      : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 border-0',
                  ].join(' ')}
                >
                  {user.isActive ? 'Active' : 'Inactive'}
                </Badge>

                {lastLogin ? (
                  <div
                    className="flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500"
                    title={lastLoginFull ?? undefined}
                  >
                    <Calendar className="h-3 w-3" />
                    <span>{lastLogin}</span>
                  </div>
                ) : (
                  <span className="text-xs text-slate-300 dark:text-slate-600 italic">
                    Never logged in
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
